import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import RequiredMark, { withRequiredMark } from '@/tiktok-platform/components/RequiredMark';
import PublishImageUploadZone from '@/tiktok-platform/modules/products/components/PublishImageUploadZone';
import CustomAttributeValueModal from '@/tiktok-platform/modules/products/components/CustomAttributeValueModal';
import { appendPrintNamesAfterHyphen } from '@/pages/ipe/workbench/utils/platformListingItemMedia';
import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import {
  resolveTiktokPrintRiskMeta,
  tiktokPrintRiskBadgeClass,
} from '@/tiktok-platform/utils/tiktokPublishPrintInfringement';
import TiktokUploadSourceMenu from '@/tiktok-platform/components/TiktokUploadSourceMenu';
import FieldHelpMark from '@/components/FieldHelpMark';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import VideoSpacePreviewModal from '@/pages/ipe/workbench/components/VideoSpacePreviewModal';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import {
  TIKTOK_PUBLISH_VIDEO_ACCEPT,
  TIKTOK_PUBLISH_VIDEO_RATIO_HINT,
  assertTiktokPublishVideoRatio,
  videoSpaceRecordToUploadFile,
} from '@/tiktok-platform/utils/tiktokVideoSpacePick';
import { resolveVideoSpacePlayUrl } from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';
import {
  TIKTOK_ATTRIBUTE_IMAGE_USE_CASE,
  TIKTOK_DETAIL_IMAGES_MAX,
  TIKTOK_MAIN_IMAGES_MAX,
  TIKTOK_MAIN_IMAGES_MIN,
  TIKTOK_MATERIAL_IMAGE_HINT,
  TIKTOK_PRODUCT_IMAGE_HINT,
} from '@/tiktok-platform/utils/tiktokProductImage';
import {
  TIKTOK_EXTERNAL_PRODUCT_ID_MAX,
  TIKTOK_SALES_ATTR_VALUE_NAME_MAX,
  TIKTOK_SELLER_SKU_MAX_LEN,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import { hideTiktokPublishField } from '@/tiktok-platform/utils/tiktokPublishUiMode';
import { officialDataAPI, tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { TIKTOK_IDENTIFIER_CODE_TYPES } from '@/tiktok-platform/utils/buildTiktokTtsCreateProductPayload';
import {
  assertLatinCustomColorName,
  toTiktokSubmitColorName,
} from '@/tiktok-platform/utils/tiktokColorNameMap';

const MAX_PRODUCT_VIDEO_BYTES = 20 * 1024 * 1024;
function tiktokMaterialHint() {
  return fixedUiTpl(
    '素材图要求：{0}。仅可上传 1 张。',
    fixedUi(TIKTOK_MATERIAL_IMAGE_HINT),
  );
}
const TIKTOK_MAIN_VIDEO_HINT = TIKTOK_PUBLISH_VIDEO_RATIO_HINT;
function tiktokProductImagesHint() {
  return fixedUiTpl(
    '商品图（主图+细节图，至少 {0} 张、合计不超过 {1} 张）：{2}。同一商品只填一组，不按颜色分图。',
    TIKTOK_MAIN_IMAGES_MIN,
    TIKTOK_MAIN_IMAGES_MAX,
    fixedUi(TIKTOK_PRODUCT_IMAGE_HINT),
  );
}
function tiktokVariantImageHint() {
  return fixedUiTpl(
    '变体图（sku_img）：按颜色维护，展示该规格在选项画廊中的样子。{0}。按平台口径以 use_case=ATTRIBUTE_IMAGE 上传。',
    fixedUi(TIKTOK_PRODUCT_IMAGE_HINT),
  );
}
/** 细节图最多张数（主图单独 1 张；合计不超过 TTS main_images 上限 9） */
const MAX_DETAIL_IMAGES = TIKTOK_DETAIL_IMAGES_MAX;

/** 印花图最多张数（站内字段，仅展示 / 侵权检测，不自动拼 SKU，不提交 TikTok） */
const MAX_PRINT_IMAGES = 10;

function batchFieldMeta(field) {
  const map = {
    price: { title: fixedUi('批量填写价格'), placeholder: fixedUi('如 9.99'), inputMode: 'decimal' },
    suggestedPrice: { title: fixedUi('批量填写建议零售价'), placeholder: fixedUi('如 19.99'), inputMode: 'decimal' },
    stock: { title: fixedUi('批量填写库存'), placeholder: fixedUi('如 100'), inputMode: 'numeric' },
    supplierSku: {
      title: fixedUi('批量填写商家 SKU'),
      placeholder: fixedUiTpl('如 SPU-COLOR-SIZE（最长 {0}，无空格）', TIKTOK_SELLER_SKU_MAX_LEN),
      inputMode: 'text',
    },
  };
  return map[field] || map.price;
}

const BATCH_TITLE_KEY = {
  price: 'publishSpec.batchPrice',
  suggestedPrice: 'publishSpec.batchMsrp',
  stock: 'publishSpec.batchStock',
  supplierSku: 'publishSpec.batchMerchantSku',
};
const BATCH_PH_KEY = {
  price: 'publishSpec.phPrice',
  suggestedPrice: 'publishSpec.phMsrp',
  stock: 'publishSpec.phStock',
  supplierSku: 'publishSpec.phSellerSku',
};

function sanitizeSkuPart(value) {
  return String(value ?? '').trim();
}

/** 从规格印花图槽位取出可拼接的名称（多张用 +；无名则空） */
export function patternNameFromPrintImages(images) {
  return (Array.isArray(images) ? images : [])
    .map((img) => sanitizeSkuPart(img?.name))
    .filter(Boolean)
    .join('+');
}

/** 弹窗未填印花名时，用规格印花图名称；都没有则为空 */
export function resolveSupplierSkuPatternName(input, printImages) {
  return sanitizeSkuPart(input) || patternNameFromPrintImages(printImages);
}

/** 用短横线拼接段位；不再预留结尾「-」（Temu 规则已废弃） */
function joinSkuPartsWithOptionalPattern(parts, patternName, { maxLen, appendPattern }) {
  const base = (Array.isArray(parts) ? parts : []).map(sanitizeSkuPart).filter(Boolean);
  if (!base.length) return '';
  const pattern = appendPattern ? sanitizeSkuPart(patternName) : '';
  const joined = pattern ? `${base.join('-')}-${pattern}` : base.join('-');
  return joined.replace(/\s+/g, '').slice(0, maxLen);
}

/** 商家 SKU（seller_sku）：建议 SPU-颜色-尺码；官方最长 50、无空格 */
export function buildMerchantSupplierSku({
  spu,
  colorCode,
  sizeLabel,
  patternName,
  includePattern = false,
}) {
  return joinSkuPartsWithOptionalPattern(
    [spu, colorCode, sizeLabel],
    patternName,
    { maxLen: TIKTOK_SELLER_SKU_MAX_LEN, appendPattern: Boolean(includePattern) },
  );
}

/** 色块侧货号草稿（站内用，便于反推色码）；最长 999 */
export function buildSkcSupplierCode({ spu, colorCode, patternName = '' }) {
  return joinSkuPartsWithOptionalPattern(
    [spu, colorCode],
    patternName,
    { maxLen: TIKTOK_EXTERNAL_PRODUCT_ID_MAX, appendPattern: Boolean(sanitizeSkuPart(patternName)) },
  );
}

/**
 * 商品级货号（表单 supplierCode → external_product_id）：
 * 款号；有图案编号时为「款号-图案编号」。不含颜色/尺码。
 */
export function buildProductSupplierCode({ spu, patternName = '' }) {
  return joinSkuPartsWithOptionalPattern(
    [spu],
    patternName,
    { maxLen: TIKTOK_EXTERNAL_PRODUCT_ID_MAX, appendPattern: Boolean(sanitizeSkuPart(patternName)) },
  );
}

/** 去掉末尾预留 - 后按短横线分段 */
export function splitSkuCodeSegments(value) {
  const raw = sanitizeSkuPart(value).replace(/-+$/, '');
  if (!raw) return [];
  return raw.split('-').map((part) => part.trim()).filter(Boolean);
}

/**
 * 用已保存的颜色编码，从「SPU-颜色编码-…」反推 SPU（允许 SPU 自身含 -）。
 * 禁止把整段货号当 SPU。
 */
export function extractSpuBeforeColorCode(codeValue, colorCode) {
  const raw = sanitizeSkuPart(codeValue).replace(/-+$/, '');
  const color = sanitizeSkuPart(colorCode);
  if (!raw || !color) return '';
  const marker = `-${color}`;
  // 从右匹配，避免 SPU 自身含相同片段时误切
  let idx = raw.lastIndexOf(marker);
  while (idx > 0) {
    const after = raw.slice(idx + marker.length);
    if (!after || after.startsWith('-')) {
      return raw.slice(0, idx);
    }
    idx = raw.lastIndexOf(marker, idx - 1);
  }
  return '';
}

/**
 * 从货号 / 商家 SKU 提取 SPU。
 * 优先：已存 colorCode + 货号/SKU 反推；否则按标准段位取第一段（不能把整段货号当 SPU）。
 */
export function inferSpuFromSkcList(skcList = []) {
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const colorCode = sanitizeSkuPart(skc?.colorCode);
    if (colorCode) {
      const fromCode = extractSpuBeforeColorCode(skc?.supplierCode, colorCode);
      if (fromCode) return fromCode;
      const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
      const fromSku = extractSpuBeforeColorCode(firstSku?.supplierSku, colorCode);
      if (fromSku) return fromSku;
    }
  }
  // 无 colorCode 时：货号至少 SPU-颜色，商家 SKU 至少 SPU-颜色-尺码 → 取第一段
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const codeParts = splitSkuCodeSegments(skc?.supplierCode);
    if (codeParts.length >= 2) return codeParts[0];
    const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
    const skuParts = splitSkuCodeSegments(firstSku?.supplierSku);
    if (skuParts.length >= 3) return skuParts[0];
    if (skuParts.length >= 1) return skuParts[0];
  }
  return '';
}

/**
 * 颜色编码优先用 skc.colorCode；否则从货号第二段或商家 SKU 第二段解析。
 * 货号：SPU-颜色[-图案]；商家 SKU：SPU-颜色-尺码[-图案]
 * （无 colorCode 且 SPU 含 - 时，第二段可能不准，故批量写入会持久化 colorCode）
 */
export function inferColorCodeFromSkc(skc) {
  const saved = sanitizeSkuPart(skc?.colorCode);
  if (saved) return saved;

  const codeParts = splitSkuCodeSegments(skc?.supplierCode);
  if (codeParts.length >= 2) return codeParts[1];

  const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
  if (firstSku) {
    const skuParts = splitSkuCodeSegments(firstSku.supplierSku);
    if (skuParts.length >= 2) return skuParts[1];
  }
  return '';
}

/** 兼容旧调用：用 SPU 前缀匹配；若前缀实为完整货号则回退到第二段色码 */
export function inferColorCodeFromSupplierSku(supplierSku, spuPrefix) {
  const raw = sanitizeSkuPart(supplierSku).replace(/-+$/, '');
  const spu = sanitizeSkuPart(spuPrefix).replace(/-+$/, '');
  if (!raw) return '';
  if (spu && raw.startsWith(`${spu}-`)) {
    const rest = raw.slice(spu.length + 1);
    const next = rest.split('-').filter(Boolean)[0] || '';
    // 前缀过长（整段货号）时 rest 往往直接是尺码，交给第二段兜底
    const parts = splitSkuCodeSegments(raw);
    if (parts.length >= 3 && next && parts[parts.length - 1] === next && parts[1] !== next) {
      return parts[1] || '';
    }
    return next;
  }
  const parts = splitSkuCodeSegments(raw);
  return parts.length >= 2 ? (parts[1] || '') : '';
}

export function inferColorCodesFromSkcList(skcList) {
  const codes = {};
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    if (!skcHasColor(skc)) return;
    const key = skcColorKey(skc);
    if (!key || codes[key]) return;
    const inferred = inferColorCodeFromSkc(skc);
    if (inferred) codes[key] = inferred;
  });
  return codes;
}

/**
 * 从货号/商家 SKU 回填图案编号。
 * 有 colorCode 时：货号去掉「SPU-颜色」后的剩余；商家 SKU 去掉「SPU-颜色-尺码」后的剩余。
 */
export function inferPatternNameFromSkcList(skcList = []) {
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const colorCode = sanitizeSkuPart(skc?.colorCode);
    const supplierCode = sanitizeSkuPart(skc?.supplierCode).replace(/-+$/, '');
    if (colorCode && supplierCode) {
      const spu = extractSpuBeforeColorCode(supplierCode, colorCode);
      if (spu) {
        const prefix = `${spu}-${colorCode}`;
        if (supplierCode === prefix) return '';
        if (supplierCode.startsWith(`${prefix}-`)) {
          return supplierCode.slice(prefix.length + 1);
        }
      }
    }
    const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
    const supplierSku = sanitizeSkuPart(firstSku?.supplierSku).replace(/-+$/, '');
    if (colorCode && supplierSku) {
      const spu = extractSpuBeforeColorCode(supplierSku, colorCode);
      if (spu) {
        const afterColor = supplierSku.slice(`${spu}-${colorCode}`.length);
        // afterColor = -尺码 或 -尺码-图案
        const restParts = afterColor.replace(/^-/, '').split('-').filter(Boolean);
        if (restParts.length >= 2) return restParts.slice(1).join('-');
      }
    }
  }
  return '';
}

export function patternNamesFromPatternText(patternName) {
  return sanitizeSkuPart(patternName)
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function appendPatternNameToSupplierSku(supplierSku, patternName) {
  const names = patternNamesFromPatternText(patternName);
  if (!names.length) return supplierSku;
  return appendPrintNamesAfterHyphen(supplierSku, names, { maxLen: TIKTOK_SELLER_SKU_MAX_LEN });
}

function resolveSkuSizeLabel(sku, sizeOptions = []) {
  if (sku?.sizeIsCustom || sku?.sizeExtraValue) {
    return attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '';
  }
  return attributeValueDisplayLabel(
    sizeOptions.find((v) => String(v.id) === String(sku?.sizeValueId)) || sku?.sizeLabel,
  ) || '';
}

function PrintInfringementResultModal({ open, riskLabel, assessedAt, body, onClose }) {
  if (!open) return null;

  const formattedAt = assessedAt
    ? (() => {
      try {
        return new Date(assessedAt).toLocaleString();
      } catch {
        return assessedAt;
      }
    })()
    : '';

  return (
    <div
      className="tko-batch-modal"
      role="dialog"
      aria-modal="true"
      aria-label={specText('publishSpec.riskTitle', fixedUi("侵权检测结果"))}
    >
      <div className="tko-batch-modal__panel tko-batch-modal__panel--infringement">
        <div className="tko-batch-modal__head">
          <strong>{specText('publishSpec.riskTitle', fixedUi("侵权检测结果"))}</strong>
          <button type="button" className="tko-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tko-batch-modal__body tko-infr-result-modal__body">
          <div className="tko-infr-result-modal__meta">
            <div><strong>{specText('publishSpec.riskStatus', fixedUi("检测状态："))}</strong>{riskLabel || '—'}</div>
            {formattedAt ? <div><strong>{specText('publishSpec.riskTime', fixedUi("检测时间："))}</strong>{formattedAt}</div> : null}
          </div>
          <div className="tko-infr-result-modal__detail">{body || specText('publishSpec.riskEmpty', fixedUi("暂无详细检测说明"))}</div>
        </div>
        <div className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={onClose}>{specText('moduleShell.close', fixedUi("关闭"))}</button>
        </div>
      </div>
    </div>
  );
}

function BatchFillModal({ field, currency, onClose, onConfirm, titleOverride }) {
  const meta = batchFieldMeta(field);
  const title = titleOverride || specText(BATCH_TITLE_KEY[field] || 'publishSpec.batchPrice', meta.title);
  const placeholder = specText(
    BATCH_PH_KEY[field] || 'publishSpec.fill',
    meta.placeholder,
    field === 'supplierSku' ? { max: TIKTOK_SELLER_SKU_MAX_LEN } : undefined,
  );
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="tko-batch-modal__panel">
        <div className="tko-batch-modal__head">
          <strong>{title}{(field === 'price' || field === 'suggestedPrice') && currency ? ` (${currency})` : ''}</strong>
          <button type="button" className="tko-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tko-batch-modal__body">
          <p className="tko-batch-modal__hint">{specText('publishSpec.applyAllRows', fixedUi("将应用到当前所有 SKU 行"))}</p>
          <input
            ref={inputRef}
            className="tko-input"
            value={value}
            inputMode={meta.inputMode}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onConfirm?.(value);
              }
            }}
          />
        </div>
        <div className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button
            type="button"
            className="tko-btn tko-btn--primary tko-btn--sm"
            onClick={() => onConfirm?.(value)}
          >
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 商品条码批量：默认统一填写，或按尺码分别填写。
 * 同一尺码的所有颜色 SKU 共用同一条码（平台要求 identifier_code 全局唯一时，
 * 多色同尺码请改用「默认」或行内单独改；默认模式也会全表写同一码）。
 */
function BarcodeBatchModal({
  sizes = [],
  defaultType = 'GTIN',
  onClose,
  onConfirm,
}) {
  const [mode, setMode] = useState('default'); // default | bySize
  const [identifierType, setIdentifierType] = useState(
    () => (TIKTOK_IDENTIFIER_CODE_TYPES.includes(defaultType) ? defaultType : 'GTIN'),
  );
  const [defaultCode, setDefaultCode] = useState('');
  const [codesBySize, setCodesBySize] = useState(() => {
    const init = {};
    sizes.forEach((s) => { init[s.key] = ''; });
    return init;
  });
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  const handleConfirm = () => {
    const type = String(identifierType || 'GTIN').trim() || 'GTIN';
    if (mode === 'default') {
      const code = String(defaultCode || '').trim();
      if (!code) {
        setError(specText('publishSpec.needBarcode', '请填写商品条码'));
        return;
      }
      onConfirm?.({ mode: 'default', identifierType: type, defaultCode: code });
      return;
    }
    if (!sizes.length) {
      setError(specText('publishSpec.pickSizeFirst', '请先选择尺码'));
      return;
    }
    const nextCodes = {};
    let filled = 0;
    sizes.forEach((s) => {
      const code = String(codesBySize[s.key] || '').trim();
      if (code) {
        nextCodes[s.key] = code;
        filled += 1;
      }
    });
    if (!filled) {
      setError(specText('publishSpec.needOneBarcode', '请至少填写一个尺码的商品条码'));
      return;
    }
    onConfirm?.({
      mode: 'bySize',
      identifierType: type,
      codesBySize: nextCodes,
    });
  };

  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true" aria-label={specText('publishSpec.batchBarcode', fixedUi("批量填写商品条码"))}>
      <div className="tko-batch-modal__panel tko-batch-modal__panel--barcode">
        <div className="tko-batch-modal__head">
          <strong>{specText('publishSpec.batchBarcode', fixedUi("批量填写商品条码"))}</strong>
          <button type="button" className="tko-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tko-batch-modal__body">
          <div
            className="tko-barcode-batch__modes"
            role="radiogroup"
            aria-label={specText('publishSpec.fillMode', '填写方式')}
          >
            <label className={`tko-barcode-batch__mode${mode === 'default' ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="tko-barcode-batch-mode"
                checked={mode === 'default'}
                onChange={() => { setMode('default'); setError(''); }}
              />
              <span>{specText('publishSpec.fillDefault', fixedUi("默认填写"))}</span>
            </label>
            <label className={`tko-barcode-batch__mode${mode === 'bySize' ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="tko-barcode-batch-mode"
                checked={mode === 'bySize'}
                onChange={() => { setMode('bySize'); setError(''); }}
              />
              <span>{specText('publishSpec.fillBySize', fixedUi("按尺码填写"))}</span>
            </label>
          </div>

          <div className="tko-field">
            <span className="tko-field__label">{specText('publishSpec.barcodeType', fixedUi("条码类型"))}</span>
            <select
              className="tko-input"
              value={identifierType}
              onChange={(e) => setIdentifierType(e.target.value)}
            >
              {TIKTOK_IDENTIFIER_CODE_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {mode === 'default' ? (
            <div className="tko-field">
              <span className="tko-field__label">{specText('publishSpec.barcode', fixedUi("商品条码"))}</span>
              <input
                ref={inputRef}
                className="tko-input"
                value={defaultCode}
                placeholder={specText('publishSpec.barcodeAllPh', '应用到全部 SKU，如 1234567890123')}
                onChange={(e) => setDefaultCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
              />
              <p className="tko-batch-modal__hint">{specText('publishSpec.barcodeAllHint', fixedUi("同一条码将写入当前所有颜色 / 尺码 SKU"))}</p>
            </div>
          ) : (
            <div className="tko-barcode-batch__sizes">
              <span className="tko-field__label">{specText('publishSpec.fillBySize', fixedUi("按尺码填写"))}</span>
              {sizes.length ? sizes.map((s) => (
                <div key={s.key} className="tko-barcode-batch__size-row">
                  <span className="tko-barcode-batch__size-name" title={s.label}>{s.label}</span>
                  <input
                    ref={s === sizes[0] ? inputRef : undefined}
                    className="tko-input"
                    value={codesBySize[s.key] || ''}
                    placeholder={specText('publishPkg.optionalPh', '选填')}
                    onChange={(e) => setCodesBySize((prev) => ({
                      ...prev,
                      [s.key]: e.target.value,
                    }))}
                  />
                </div>
              )) : (
                <p className="tko-batch-modal__hint">{specText('publishSpec.noSizes', fixedUi("暂无已选尺码"))}</p>
              )}
              <p className="tko-batch-modal__hint">{specText('publishSpec.barcodeSizeHint', fixedUi("同一尺码下的各颜色 SKU 共用该条码；未填的尺码不改动"))}</p>
            </div>
          )}
          {error ? <p className="tko-field__error" role="alert">{error}</p> : null}
        </div>
        <div className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={handleConfirm}>
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 货号 / 商家 SKU 共用批量：SPU + 颜色编码 + 图案编号；两边同步写入 */
function SupplierCodeBatchModal({
  colors = [],
  defaultSpu = '',
  skcList = [],
  title,
  onClose,
  onConfirm,
}) {
  const initialColorCodes = useMemo(() => inferColorCodesFromSkcList(skcList), [skcList]);
  const resolvedDefaultSpu = useMemo(() => {
    const fromList = inferSpuFromSkcList(skcList);
    if (fromList) return fromList;
    const raw = sanitizeSkuPart(defaultSpu).replace(/-+$/, '');
    if (!raw) return '';
    // defaultSpu 常被误传成完整货号：用任一已推断色码反推 SPU
    const sampleColor = Object.values(initialColorCodes).find(Boolean);
    if (sampleColor) {
      const extracted = extractSpuBeforeColorCode(raw, sampleColor);
      if (extracted) return extracted;
    }
    // 仍像「SPU-颜色」货号时，不要整段塞进 SPU
    const parts = splitSkuCodeSegments(raw);
    if (parts.length >= 2) return parts[0];
    return raw;
  }, [skcList, defaultSpu, initialColorCodes]);

  const initialSpuRef = useRef(resolvedDefaultSpu);
  const initialColorCodesRef = useRef(initialColorCodes);
  const initialPatternRef = useRef(inferPatternNameFromSkcList(skcList));
  const [spu, setSpu] = useState(() => initialSpuRef.current);
  const [patternName, setPatternName] = useState(() => initialPatternRef.current);
  const [includePatternInSku, setIncludePatternInSku] = useState(
    () => Boolean(initialPatternRef.current),
  );
  const [colorCodes, setColorCodes] = useState(() => {
    const inferred = initialColorCodesRef.current;
    const init = {};
    colors.forEach((c) => { init[c.key] = inferred[c.key] || ''; });
    return init;
  });
  const [error, setError] = useState('');
  const spuRef = useRef(null);

  useEffect(() => {
    spuRef.current?.focus();
  }, []);

  const handleConfirm = () => {
    const nextSpu = sanitizeSkuPart(spu);
    const nextPattern = sanitizeSkuPart(patternName);
    const inferred = inferColorCodesFromSkcList(skcList);
    const mergedCodes = {};
    colors.forEach((c) => {
      mergedCodes[c.key] = sanitizeSkuPart(colorCodes[c.key]) || inferred[c.key] || '';
    });

    if (!nextSpu) {
      setError(specText('publishSpec.needSpu', '请填写 SPU'));
      return;
    }
    if (!colors.length) {
      setError(specText('publishSpec.pickMainColors', '请先选择主规格颜色'));
      return;
    }
    const missing = colors.filter((c) => !mergedCodes[c.key]);
    if (missing.length) {
      setError(specText('publishSpec.needColorCodes', `请填写颜色编码：${missing.map((c) => c.label).slice(0, 3).join('、')}`, { names: missing.map((c) => c.label).slice(0, 3).join('、') }));
      return;
    }
    if (includePatternInSku && !nextPattern) {
      setError(specText('publishSpec.needPattern', '已勾选在商家 SKU 添加图案编号，请填写图案编号'));
      return;
    }
    onConfirm?.({
      spu: nextSpu,
      patternName: nextPattern,
      colorCodes: mergedCodes,
      includePatternInSku: Boolean(includePatternInSku),
    });
  };

  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="tko-batch-modal__panel tko-batch-modal__panel--supplier-sku">
        <div className="tko-batch-modal__head">
          <strong>{title}</strong>
          <button type="button" className="tko-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tko-batch-modal__body">
          <div className="tko-field">
            <span className="tko-field__label">SPU <RequiredMark /></span>
            <input
              ref={spuRef}
              className="tko-input"
              value={spu}
              placeholder={specText('publishSpec.phSpu', '如 1001')}
              onChange={(e) => setSpu(e.target.value)}
            />
          </div>
          <div className="tko-supplier-sku-colors">
            <span className="tko-field__label">{specText('publishSpec.colorCode', fixedUi("颜色编码"))} <RequiredMark /></span>
            {colors.length ? colors.map((c) => (
              <div key={c.key} className="tko-supplier-sku-color-row">
                <span className="tko-supplier-sku-color-row__name" title={c.label}>{c.label}</span>
                <input
                  className="tko-input"
                  value={colorCodes[c.key] || ''}
                  placeholder={specText('publishSpec.phWh', '如 WH')}
                  onChange={(e) => setColorCodes((prev) => ({ ...prev, [c.key]: e.target.value }))}
                />
              </div>
            )) : (
              <p className="tko-batch-modal__hint">{specText('publishSpec.noColors', fixedUi("暂无已选颜色"))}</p>
            )}
          </div>
          <div className="tko-field">
            <span className="tko-field__label">{specText('publishSpec.patternNo', fixedUi("图案编号"))}</span>
            <input
              className="tko-input"
              value={patternName}
              placeholder={specText('publishSpec.patternOptional', fixedUi("可选"))}
              onChange={(e) => setPatternName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          </div>
          <label className="tko-supplier-sku-pattern-opt">
            <input
              type="checkbox"
              checked={includePatternInSku}
              onChange={(e) => setIncludePatternInSku(e.target.checked)}
            />
            <span>{specText('publishSpec.addPatternToSku', fixedUi("在商家 SKU 添加图案编号"))}</span>
          </label>
          {error ? <p className="tko-field__error" role="alert">{error}</p> : null}
        </div>
        <div className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={handleConfirm}>
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchHeader({ children, onBatch }) {
  const { t } = useTranslation('workbench');
  return (
    <th>
      <span className="tko-supply-th">
        <span>{children}</span>
        <button type="button" className="tko-supply-th__batch" onClick={onBatch}>
          {t('publishSpec.batch')}
        </button>
      </span>
    </th>
  );
}

// 槽位文案为站内自定义，非平台接口字段名（展示前去掉「(中文)」等脏标记）
function optionName(v) {
  return attributeValueDisplayLabel(v) || String(v?.id || '');
}

function isCustomSpecKey(key) {
  return String(key || '').startsWith('custom:');
}

function customLabelFromKey(key) {
  try {
    return decodeURIComponent(String(key).slice('custom:'.length));
  } catch {
    return String(key).slice('custom:'.length);
  }
}

export function makeCustomSpecKey(label) {
  return `custom:${encodeURIComponent(String(label || '').trim())}`;
}

/** 批量条码按尺码对齐用的稳定 key */
export function skuSizeBatchKey(sku) {
  if (!sku) return '';
  if (sku.sizeIsCustom || sku.sizeExtraValue) {
    return makeCustomSpecKey(sku.sizeExtraValue || sku.sizeLabel || '');
  }
  return String(sku.sizeValueId || '').trim();
}

/** 从当前 SKU 行收集去重尺码（批量条码「按尺码」表） */
export function collectBarcodeBatchSizes(skcList = [], sizeOptions = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    (skc?.skuRows || []).forEach((sku) => {
      const key = skuSizeBatchKey(sku);
      const label = resolveSkuSizeLabel(sku, sizeOptions);
      if (!key || !label || seen.has(key)) return;
      seen.add(key);
      out.push({ key, label });
    });
  });
  return out;
}

/** SKC 颜色在选择器中的稳定 key（平台 valueId 或 custom:xxx） */
export function skcColorKey(skc) {
  if (!skc) return '';
  if (skc.colorIsCustom || skc.colorExtraValue) {
    return makeCustomSpecKey(skc.colorExtraValue || skc.colorLabel || '');
  }
  return String(skc.colorValueId || '');
}

export function skcHasColor(skc) {
  return Boolean(skc && (skc.colorValueId || String(skc.colorExtraValue || '').trim() || String(skc.colorLabel || '').trim()));
}

/**
 * 规格值选择。
 * - 默认：多选勾选（尺码）
 * - allowCustom + addMode：点选即添加（可连续选）；回车可添加自定义颜色
 *   （对齐商家后台「请选择或自定义」；OpenAPI 主规格自定义须用 custom_attribute_value，禁止 attribute_value_id=0）
 */
export function SpecValuePicker({
  options = [],
  value = [],
  onChange,
  placeholder = fixedUi('请选择或搜索（可多选）'),
  disabled = false,
  emptyText = fixedUi('暂无平台选项'),
  disabledHint = fixedUi('暂不可选'),
  allowCustom = false,
  addMode = false,
  /** key → 展示名；属性模板未就绪时用平台回填的 attributeValueName，禁止直接显示 valueId */
  labelByKey = {},
  /** 平台 has_permission=1：展示「申请新增」并走 add-custom-attribute-value */
  allowPlatformCustom = false,
  /** 站内颜色库等预置候选（选中后按自定义值提交）；[{ id, name, colorCode?, group? }] */
  libraryOptions = [],
  libraryLabel = '颜色库',
  shopId = '',
  categoryId = '',
  attributeId = '',
  attributeLabel = '',
  onPlatformCustomAdded,
  /** 自定义值须英文（TikTok 颜色：常见中文自动映射，其余含中文则拒绝） */
  requireLatinCustomColor = false,
}) {
  const { t } = useTranslation('workbench');
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  const [extraOptions, setExtraOptions] = useState([]);

  useEffect(() => {
    setExtraOptions([]);
    setCustomOpen(false);
  }, [attributeId, categoryId]);

  const normalizedOptions = useMemo(() => {
    const base = (options || []).map((opt) => ({ ...opt, id: String(opt.id) }));
    const seen = new Set(base.map((opt) => opt.id));
    const extras = extraOptions.filter((opt) => opt?.id && !seen.has(String(opt.id)));
    const merged = extras.length
      ? [...base, ...extras.map((opt) => ({ ...opt, id: String(opt.id) }))]
      : base;
    // 颜色库等预置候选：与平台 values 去重后并入，并按 group='library' 分组展示
    const seenAll = new Set(merged.map((opt) => opt.id));
    const library = (libraryOptions || [])
      .map((opt) => ({ ...opt, id: String(opt.id), group: 'library' }))
      .filter((opt) => opt.id && !seenAll.has(opt.id));
    return library.length ? [...merged, ...library] : merged;
  }, [options, extraOptions, libraryOptions]);

  const selectedKeys = useMemo(
    () => (Array.isArray(value) ? value.map(String).filter(Boolean) : []),
    [value],
  );

  const selectedTags = useMemo(() => selectedKeys.map((key) => {
    if (isCustomSpecKey(key)) {
      return {
        key,
        label: attributeValueDisplayLabel(customLabelFromKey(key)) || customLabelFromKey(key),
        custom: true,
      };
    }
    const opt = normalizedOptions.find((item) => item.id === key);
    const fallback = labelByKey[key];
    return {
      key,
      label: opt
        ? optionName(opt)
        : (attributeValueDisplayLabel(fallback) || fallback || key),
      custom: false,
    };
  }), [selectedKeys, normalizedOptions, labelByKey]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((opt) => optionName(opt).toLowerCase().includes(q));
  }, [normalizedOptions, keyword]);

  const platformFiltered = useMemo(
    () => filtered.filter((opt) => opt.group !== 'library'),
    [filtered],
  );
  const libraryFiltered = useMemo(
    () => filtered.filter((opt) => opt.group === 'library'),
    [filtered],
  );
  /** 颜色库可能上百条：未搜索时只渲染前 N 条，避免面板过长 */
  const LIBRARY_RENDER_LIMIT = 60;
  const libraryOverflow = !keyword.trim() && libraryFiltered.length > LIBRARY_RENDER_LIMIT;
  const libraryVisible = libraryOverflow
    ? libraryFiltered.slice(0, LIBRARY_RENDER_LIMIT)
    : libraryFiltered;

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxHeight = Math.min(280, Math.max(140, window.innerHeight - rect.bottom - 12));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
      maxHeight,
      zIndex: 14000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, selectedKeys.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const resolveCustomSpecKey = (rawLabel) => {
    const text = String(rawLabel || '').trim();
    if (!text) return '';
    if (!requireLatinCustomColor) return makeCustomSpecKey(text);
    const check = assertLatinCustomColorName(text);
    if (!check.ok) {
      showToast(check.message, 'warning');
      return '';
    }
    return makeCustomSpecKey(check.name);
  };

  const addKey = (key) => {
    const nextKey = String(key || '').trim();
    if (!nextKey) return;
    if (selectedKeys.includes(nextKey)) return;
    onChange([...selectedKeys, nextKey]);
  };

  const toggle = (id) => {
    let nextId = String(id);
    if (requireLatinCustomColor && isCustomSpecKey(nextId)) {
      const resolved = resolveCustomSpecKey(customLabelFromKey(nextId));
      if (!resolved) return;
      nextId = resolved;
    }
    if (addMode) {
      addKey(nextId);
      return;
    }
    const set = new Set(selectedKeys);
    if (set.has(nextId)) set.delete(nextId);
    else set.add(nextId);
    onChange([...set]);
  };

  const remove = (key) => onChange(selectedKeys.filter((item) => item !== String(key)));

  const addCustomFromKeyword = () => {
    // 输入框已 maxLength=value_name 上限 50
    const text = keyword.trim();
    if (!text) return false;
    const matched = normalizedOptions.find((opt) => optionName(opt).toLowerCase() === text.toLowerCase());
    if (matched) {
      if (requireLatinCustomColor && isCustomSpecKey(matched.id)) {
        const resolved = resolveCustomSpecKey(customLabelFromKey(matched.id));
        if (!resolved) return false;
        addKey(resolved);
      } else {
        addKey(matched.id);
      }
      setKeyword('');
      return true;
    }
    if (allowCustom) {
      const key = requireLatinCustomColor
        ? resolveCustomSpecKey(text)
        : makeCustomSpecKey(text);
      if (!key) return false;
      addKey(key);
      setKeyword('');
      return true;
    }
    return false;
  };

  const canOpen = !disabled && (normalizedOptions.length > 0 || allowCustom);
  const inputPlaceholder = disabled
    ? disabledHint
    : (normalizedOptions.length === 0 && !allowCustom
      ? emptyText
      : placeholder);
  const canPlatformCustom = Boolean(
    allowPlatformCustom && shopId && categoryId && attributeId && !disabled,
  );

  const onPlatformAdded = (opt) => {
    if (!opt?.id) return;
    setExtraOptions((prev) => (
      prev.some((item) => String(item.id) === String(opt.id))
        ? prev
        : [...prev, { id: String(opt.id), name: opt.name, nameCn: opt.nameCn || opt.name }]
    ));
    onPlatformCustomAdded?.(opt);
    addKey(String(opt.id));
  };

  const renderOption = (opt) => {
    const checked = selectedKeys.includes(opt.id);
    return (
      <button
        key={opt.id}
        type="button"
        className={`tko-spec-picker__option${checked ? ' is-on' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          toggle(opt.id);
          if (addMode) setKeyword('');
        }}
      >
        {!addMode ? (
          <input type="checkbox" readOnly checked={checked} tabIndex={-1} />
        ) : null}
        {opt.colorCode ? (
          <i className="tko-spec-picker__swatch" style={{ background: opt.colorCode }} aria-hidden />
        ) : null}
        <span>{optionName(opt)}</span>
        {opt.colorCode ? <em className="tko-spec-picker__code">{opt.colorCode}</em> : null}
        {addMode && checked ? <em className="tko-spec-picker__added">{t('publishSpec.added')}</em> : null}
      </button>
    );
  };

  const panel = open && canOpen ? createPortal(
    <div
      ref={panelRef}
      className="tko-spec-picker__panel tko-spec-picker__panel--portal"
      style={panelStyle}
    >
      {filtered.length === 0 ? (
        <div className="tko-spec-picker__empty">
          {allowCustom && keyword.trim()
            ? t('publishSpec.enterCustom', { name: keyword.trim() })
            : (emptyText || t('productCopy.emptyOptions'))}
        </div>
      ) : (
        <div className="tko-spec-picker__list">
          {platformFiltered.map(renderOption)}
          {libraryFiltered.length > 0 ? (
            <>
              <div className="tko-spec-picker__group">
                {libraryLabel}
                {libraryOverflow ? t('publishSpec.libraryOverflow', { count: libraryFiltered.length }) : ''}
              </div>
              {libraryVisible.map(renderOption)}
            </>
          ) : null}
        </div>
      )}
      <div className="tko-spec-picker__footer">
        {selectedKeys.length > 0 ? (
          <button
            type="button"
            className="tko-btn tko-btn--ghost tko-btn--sm"
            onClick={() => onChange([])}
          >
            {t('publishAttr.clear')}
          </button>
        ) : <span />}
        <button
          type="button"
          className="tko-btn tko-btn--secondary tko-btn--sm"
          onClick={() => {
            setOpen(false);
            setKeyword('');
          }}
        >
          {t('returnList.completed')}
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      className={`tko-spec-picker${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      ref={rootRef}
    >
      <div className="tko-spec-picker__row">
        {selectedTags.length > 0 ? (
          <div className="tko-spec-picker__tags">
            {selectedTags.map((tag) => (
              <span key={tag.key} className={`tko-spec-picker__tag${tag.custom ? ' is-custom' : ''}`}>
                <em>{tag.label}</em>
                <button
                  type="button"
                  aria-label={t('productCopy.remove')}
                  disabled={disabled}
                  onClick={() => remove(tag.key)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div
          className="tko-spec-picker__control"
          ref={controlRef}
          onClick={() => {
            if (canOpen) setOpen(true);
          }}
        >
          <input
            type="text"
            autoComplete="off"
            className="tko-spec-picker__search"
            value={keyword}
            maxLength={allowCustom ? TIKTOK_SALES_ATTR_VALUE_NAME_MAX : undefined}
            disabled={disabled || (!allowCustom && normalizedOptions.length === 0)}
            placeholder={inputPlaceholder}
            onFocus={() => {
              if (canOpen) setOpen(true);
            }}
            onChange={(e) => {
              setKeyword(e.target.value);
              if (canOpen) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (addMode || allowCustom) {
                  if (!addCustomFromKeyword() && filtered[0]) addKey(filtered[0].id);
                } else if (filtered[0]) {
                  toggle(filtered[0].id);
                }
                setKeyword('');
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setKeyword('');
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="tko-spec-picker__caret" aria-hidden>▾</span>
        </div>
      </div>
      {panel}
      {canPlatformCustom ? (
        <div className="tko-attr-custom-hint">
          <span>{t('publishAttr.noValue')}</span>
          <button
            type="button"
            className="tko-attr-custom-hint__link"
            onClick={() => setCustomOpen(true)}
          >
            {t('publishAttr.applyNew')}
          </button>
        </div>
      ) : null}
      <CustomAttributeValueModal
        open={customOpen}
        shopId={shopId}
        categoryId={categoryId}
        attributeId={attributeId}
        attributeLabel={attributeLabel}
        onClose={() => setCustomOpen(false)}
        onAdded={onPlatformAdded}
      />
    </div>
  );
}

/**
 * 规格供应：主规格 / 其他规格（名称来自类目销售属性模板，不写死「颜色/尺码」）
 * → 自动生成主规格图行 + 供应信息表
 */
function specText(key, fallback, options) {
  const fb = fallback == null || typeof fallback !== 'string' ? fallback : fixedUi(fallback, options);
  if (!i18n.isInitialized) return fb;
  const raw = i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
  return raw == null || typeof raw !== 'string' ? raw : fixedUi(raw, options);
}

export function specSupplyPriceLabel(priceMode, currency) {
  const code = String(currency || '').trim();
  if (priceMode === 'sale') {
    return code
      ? specText('publishSpec.salePriceCode', `售价(${code}) *`, { code })
      : specText('publishSpec.salePrice', '售价 *');
  }
  if (priceMode === 'cost') {
    return code
      ? specText('publishSpec.costPriceCode', `成本价(${code}) *`, { code })
      : specText('publishSpec.costPrice', '成本价 *');
  }
  return code
    ? specText('publishSpec.priceCode', `价格(${code}) *`, { code })
    : specText('publishSpec.price', '价格 *');
}

export default function SpecSupplySection({
  colorAttr,
  sizeAttr,
  skcList = [],
  selectedSizeIds = [],
  shopId,
  categoryId = '',
  customAttrPerm = {},
  onCustomValueAdded,
  onColorsChange,
  onSizesChange,
  onUpdateSkc,
  onUpdateSku,
  onRemoveSku,
  onBatchUpdateSku,
  onSyncSkuSupply,
  /** 商品级主图 / 细节图（不按颜色分） */
  mainImage = null,
  detailImages = [],
  onMainImageChange,
  onDetailImagesChange,
  printImages = [],
  onPrintImagesChange,
  currency = '',
  currencyPending = false,
  attrLoading = false,
  /** 编辑已审商品：价走专用接口，UI 提示文案 */
  isEdit = false,
  /** 'cost' | 'sale' | '' */
  priceMode = '',
  priceEditable = true,
  priceLockedHint = '',
  /** 批量商家 SKU：SPU 默认值（如货号） */
  defaultSupplierSkuSpu = '',
  /** 批量确定后回写商品级货号（款号 / 款号-图案编号） */
  onProductSupplierCodeChange,
  printInfringement = null,
  printInfringementBusy = false,
  onAssessPrintInfringement,
  infringementCreditHint = '',
  /**
   * 官方产品模板：无店铺，上传只落 IPE，跳过 TikTok 格式转换。
   * 印花图在组件内始终 skipTiktokConvert（只落 OSS，不调平台）。
   */
  skipTiktokConvert = false,
  /** 官方产品模板：隐藏必填星号（保存不拦业务必填） */
  hideRequiredMarks = false,
  hidePrintImages = false,
  /** Tiktok 素材图（bg.goods.add materialImgUrl） */
  materialImage = null,
  materialImgError = '',
  onMaterialImagesChange,
  /** Tiktok 主图视频 productCarouseVideoReqList */
  tiktokVideos = [],
  videoError = '',
  onVideosChange,
  /** 素材图下方插槽（模特 / 官方尺寸信息） */
  afterMaterialContent = null,
}) {
  const { t } = useTranslation('workbench');
  const hideMaterialImage = hideTiktokPublishField('materialImage');
  const reqMark = hideRequiredMarks ? null : <RequiredMark />;
  const videoInputRef = useRef(null);
  const videoUploadAnchorRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoMenuOpen, setVideoMenuOpen] = useState(false);
  const [videoLibraryOpen, setVideoLibraryOpen] = useState(false);
  const [videoLibrarySelection, setVideoLibrarySelection] = useState([]);
  const [videoPreviewTarget, setVideoPreviewTarget] = useState(null);
  // 规格选项一律取自平台（Get Attributes 销售属性的 values），不再做类目级硬编码预设：
  // 童装/鞋类等类目塞入「常用成人尺码」会与平台口径冲突（童装出现 XS~4XL）。
  const colorOptions = colorAttr?.values || [];
  const sizeOptions = sizeAttr?.values || [];
  // 平台颜色/尺码的 SALES_PROPERTY 属「商家自己填写」型（values 常为空、is_customizable=true），
  // 因此这里始终开放自定义输入（回车添加，提交按 value_name 走自定义值，颜色提交前转英文）；
  // 颜色额外可从站内颜色库选取，选中即作为自定义值，无需等平台值ID。
  const [colorLibraryOptions, setColorLibraryOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    officialDataAPI.listDesignColors()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.colors) ? res.colors : [];
        const seen = new Set();
        const list = [];
        rows.forEach((row) => {
          const label = String(row?.colorLabel || '').trim();
          if (!label) return;
          // 颜色库中文名映射为英文后入库；无法映射的中文跳过（平台不接受）
          const submitLabel = toTiktokSubmitColorName(label);
          if (!submitLabel || seen.has(submitLabel)) return;
          seen.add(submitLabel);
          list.push({
            id: makeCustomSpecKey(submitLabel),
            name: submitLabel !== label ? `${label}（${submitLabel}）` : submitLabel,
            colorCode: String(row?.colorCode || '').trim(),
          });
        });
        setColorLibraryOptions(list);
      })
      .catch(() => {
        if (!cancelled) setColorLibraryOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const priceLabel = specSupplyPriceLabel(priceMode, currency);
  const priceHeadLabel = hideRequiredMarks
    ? String(priceLabel || '').replace(/\s*\*\s*$/, '')
    : withRequiredMark(priceLabel);
  const priceBatchTitle = priceMode === 'sale'
    ? t('publishSpec.batchSale')
    : priceMode === 'cost'
      ? t('publishSpec.batchCost')
      : t('publishSpec.batchPrice');
  const selectedColorKeys = skcList.map(skcColorKey).filter(Boolean);
  const colorLabelByKey = useMemo(() => {
    const map = {};
    (skcList || []).forEach((skc) => {
      const key = skcColorKey(skc);
      const name = String(skc?.colorLabel || skc?.colorExtraValue || '').trim();
      if (key && name) map[key] = name;
    });
    return map;
  }, [skcList]);
  const platformSizeIds = (selectedSizeIds || []).map(String).filter(Boolean);
  const sizeLabelByKey = useMemo(() => {
    const map = {};
    (skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        if (sku.sizeIsCustom || sku.sizeExtraValue) {
          const key = makeCustomSpecKey(sku.sizeExtraValue || sku.sizeLabel || '');
          const name = String(sku.sizeLabel || sku.sizeExtraValue || '').trim();
          if (key && name) map[key] = name;
        } else if (sku.sizeValueId && sku.sizeLabel) {
          map[String(sku.sizeValueId)] = sku.sizeLabel;
        }
      });
    });
    return map;
  }, [skcList]);
  const coloredSkcs = skcList.filter(skcHasColor);
  const hasColors = coloredSkcs.length > 0;
  const [batchField, setBatchField] = useState(null);
  const [printRiskModalOpen, setPrintRiskModalOpen] = useState(false);
  const printRiskMeta = resolveTiktokPrintRiskMeta(printInfringement);

  const rememberImageSpaceRows = (rows) => {
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  };

  const uploadVideoFile = async (file, options = {}) => {
    if (!file || !shopId) return;
    if (file.size > MAX_PRODUCT_VIDEO_BYTES) {
      showToast(specText('publishSpec.videoTooBig20', '视频需 ≤20MB'), 'warning');
      return;
    }
    const mime = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    const looksVideo = mime.startsWith('video/')
      || /\.(mp4|mov|m4v|webm|avi)$/i.test(name);
    if (!looksVideo) {
      showToast(specText('publishSpec.videoPick', '主图视频请选择视频文件（MP4/MOV 等）'), 'warning');
      return;
    }
    setVideoBusy(true);
    try {
      await assertTiktokPublishVideoRatio(file);
      const res = await tiktokPlatformAPI.uploadProductVideo(shopId, file, {
        fromVideoSpace: options.fromVideoSpace === true,
      });
      if (!res?.videoUrl && !res?.vid) {
        throw new Error(specText('publishSpec.videoNoResult', '视频上传未返回可用结果'));
      }
      onVideosChange?.([{
        vid: res.vid,
        videoUrl: res.videoUrl,
        coverUrl: res.coverUrl,
        width: res.width,
        height: res.height,
        // 视频空间选用时后端 ipeUrl 为空，保留成片地址便于草稿回填后预览
        ipeUrl: res.ipeUrl || options.sourceIpeUrl || '',
      }]);
      showToast(specText('publishSpec.videoOk', '视频上传成功'), 'success');
    } catch (err) {
      showToast(err?.message || specText('publishSpec.videoFail', '视频上传失败'), 'error');
    } finally {
      setVideoBusy(false);
    }
  };

  const handleVideoLibraryConfirm = async (ids) => {
    setVideoLibraryOpen(false);
    setVideoLibrarySelection([]);
    const id = Array.isArray(ids) ? ids[0] : ids;
    if (!id) return;
    const row = imageSpaceRowsRef.current.find((item) => String(item?.id) === String(id));
    if (!row) {
      showToast(specText('publishSpec.videoMissing', '未找到所选视频空间文件'), 'error');
      return;
    }
    try {
      const sourceIpeUrl = resolveVideoSpacePlayUrl(row)
        || String(row?.outputVideoUrl || row?._playUrl || '').trim();
      const file = await videoSpaceRecordToUploadFile(row, 'video.mp4');
      await uploadVideoFile(file, { fromVideoSpace: true, sourceIpeUrl });
    } catch (err) {
      showToast(err?.message || specText('publishSpec.videoSpaceFail', '视频空间读取失败'), 'error');
    }
  };

  const supplierSkuColorOptions = useMemo(() => (
    (skcList || [])
      .filter(skcHasColor)
      .map((skc) => ({
        key: skcColorKey(skc),
        label: attributeValueDisplayLabel(skc.colorLabel || skc.colorExtraValue) || skc.colorLabel || specText('publishSpec.unnamedColor', '未命名颜色'),
      }))
      .filter((row) => row.key)
  ), [skcList]);

  const barcodeSizeOptions = useMemo(
    () => collectBarcodeBatchSizes(skcList, sizeAttr?.values || []),
    [skcList, sizeAttr?.values],
  );

  const applyBatch = (raw) => {
    const text = String(raw ?? '').trim();
    if (!batchField) return;
    if (
      batchField === 'supplierSku'
      || batchField === 'supplierCode'
      || batchField === 'identifierCode'
    ) return;
    if (!text) {
      setBatchField(null);
      return;
    }
    onBatchUpdateSku?.(batchField === 'suggestedPrice'
      ? {
        suggestedPrice: text,
        suggestedPriceCurrencyType: currency || 'USD',
      }
      : { [batchField]: text });
    setBatchField(null);
  };

  const applyBarcodeBatch = (payload) => {
    const mode = payload?.mode === 'bySize' ? 'bySize' : 'default';
    const identifierType = String(payload?.identifierType || 'GTIN').trim() || 'GTIN';
    if (mode === 'default') {
      const code = String(payload?.defaultCode || '').trim();
      if (!code) {
        setBatchField(null);
        return;
      }
      onBatchUpdateSku?.(() => ({
        identifierType,
        identifierCode: code,
      }));
      setBatchField(null);
      return;
    }
    const codesBySize = payload?.codesBySize && typeof payload.codesBySize === 'object'
      ? payload.codesBySize
      : {};
    onBatchUpdateSku?.((_skc, sku) => {
      const key = skuSizeBatchKey(sku);
      const code = String(codesBySize[key] || '').trim();
      if (!code) return null;
      return { identifierType, identifierCode: code };
    });
    setBatchField(null);
  };

  const applySupplierCodeBatch = (payload) => {
    const {
      spu,
      patternName,
      colorCodes,
      includePatternInSku,
    } = payload || {};
    // 商品级货号：款号；有图案编号则「款号-图案编号」（与是否勾选写入商家 SKU 无关）
    if (typeof onProductSupplierCodeChange === 'function') {
      const nextProductCode = buildProductSupplierCode({ spu, patternName });
      if (nextProductCode) onProductSupplierCodeChange(nextProductCode);
    }
    if (typeof onUpdateSkc === 'function' && colorCodes) {
      (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
        if (!skcHasColor(skc)) return;
        const colorKey = skcColorKey(skc);
        const colorCode = sanitizeSkuPart(colorCodes?.[colorKey]);
        if (!colorCode) return;
        onUpdateSkc(skc.id, {
          colorCode,
          supplierCode: buildSkcSupplierCode({
            spu,
            colorCode,
            patternName,
          }),
        });
      });
    }
    onBatchUpdateSku?.((skc, sku) => {
      const colorKey = skcColorKey(skc);
      const colorCode = sanitizeSkuPart(colorCodes?.[colorKey]);
      const sizeName = resolveSkuSizeLabel(sku, sizeOptions);
      if (!colorCode || !sizeName) return null;
      return {
        // 未勾选添加图案编号：SPU-颜色-尺码（末尾不加 -）
        supplierSku: buildMerchantSupplierSku({
          spu,
          colorCode,
          sizeLabel: sizeName,
          patternName,
          includePattern: Boolean(includePatternInSku),
        }),
      };
    });
    setBatchField(null);
  };

  return (
    <div className="tko-spec-supply">
      {/* —— 主规格：销售属性 + 商品图 —— */}
      <div className="tko-spec-block">
        {/* 印花图：主规格标题上方（对齐 SHEIN） */}
        {!hidePrintImages ? (
          <div className="tko-spec-print">
            <div className="tko-spec-print__head">
              <span className="tko-field__label tko-th-with-help">
                {t('publishSpec.print')}
                <FieldHelpMark text={t('publishSpec.printHelpTt')} />
              </span>
              <div className="tko-spec-print__actions">
                <button
                  type="button"
                  className="tko-btn tko-btn--danger tko-btn--sm tko-spec-print__risk-btn"
                  disabled={printInfringementBusy || printRiskMeta.meta.assessing}
                  title={infringementCreditHint || undefined}
                  onClick={() => { void onAssessPrintInfringement?.(); }}
                >
                  {printRiskMeta.meta.assessing ? t('publishSpec.assessing') : t('publishSpec.infringement')}
                </button>
                <button
                  type="button"
                  className={`tko-infr-risk-btn${printRiskMeta.canOpenRisk ? ' is-clickable' : ''}`}
                  disabled={!printRiskMeta.canOpenRisk}
                  onClick={() => {
                    if (!printRiskMeta.canOpenRisk) return;
                    setPrintRiskModalOpen(true);
                  }}
                >
                  <span className={`tko-infr-risk-badge ${tiktokPrintRiskBadgeClass(printRiskMeta.meta.risk, printRiskMeta.meta.assessing)}`}>
                    {printRiskMeta.riskLabel}
                  </span>
                  {printRiskMeta.canOpenRisk ? (
                    <span className="tko-infr-risk-hint">{t('publishSpec.viewRisk')}</span>
                  ) : null}
                </button>
              </div>
            </div>
            <PublishImageUploadZone
              compact
              labelInside
              label={t('publishSpec.print')}
              imageType={2}
              multiple
              maxCount={MAX_PRINT_IMAGES}
              uploadSlotFirst
              prependNew
              reorderable
              enforceTiktokRules={false}
              skipTiktokConvert
              value={Array.isArray(printImages) ? printImages : []}
              shopId={shopId}
              onChange={(imgs) => onPrintImagesChange?.(
                Array.isArray(imgs) ? imgs.slice(0, MAX_PRINT_IMAGES) : [],
              )}
            />
          </div>
        ) : null}

        <h4 className="tko-publish__subhead">{t('publishSpec.mainSpec')}</h4>

        <div className="tko-spec-row">
          <div className="tko-spec-attr">
            <span className="tko-field__label">{colorLabel}{reqMark ? <> {reqMark}</> : null}</span>
            <SpecValuePicker
              options={colorOptions}
              value={selectedColorKeys}
              onChange={onColorsChange}
              labelByKey={colorLabelByKey}
              placeholder={attrLoading
                ? t('returnList.loading')
                : (colorLibraryOptions.length
                  ? t('publishSpec.pickCustomLib', { label: colorLabel })
                  : t('publishSpec.pickCustom', { label: colorLabel }))}
              disabled={attrLoading || !colorAttr}
              emptyText={t('publishSpec.emptyCustomLib')}
              disabledHint={attrLoading ? t('returnList.loading') : t('publishSpec.loadAttr', { label: colorLabel })}
              allowCustom
              libraryOptions={colorLibraryOptions}
              libraryLabel={t('publishSpec.colorLib')}
              addMode
              requireLatinCustomColor
              allowPlatformCustom={!!customAttrPerm[String(colorAttr?.attributeId || '')]}
              shopId={shopId}
              categoryId={categoryId}
              attributeId={colorAttr?.attributeId || ''}
              attributeLabel={colorLabel}
              onPlatformCustomAdded={(opt) => onCustomValueAdded?.(colorAttr?.attributeId, opt)}
            />
          </div>

          <div className="tko-spec-attr">
            <span className="tko-field__label">{sizeLabel}{(!hideRequiredMarks && sizeAttr?.required) ? <RequiredMark /> : null}</span>
            <SpecValuePicker
              options={sizeOptions}
              value={platformSizeIds}
              onChange={onSizesChange}
              labelByKey={sizeLabelByKey}
              placeholder={attrLoading ? t('returnList.loading') : t('publishSpec.pickCustom', { label: sizeLabel })}
              disabled={attrLoading || !hasColors || !sizeAttr}
              emptyText={t('publishSpec.emptySizeEnter')}
              disabledHint={!hasColors ? t('publishSpec.pickFirst', { label: colorLabel }) : (!sizeAttr ? t('publishSpec.noAttr', { label: sizeLabel }) : t('publishSpec.unavailable'))}
              allowCustom
              addMode
              shopId={shopId}
              categoryId={categoryId}
              attributeId={sizeAttr?.attributeId || ''}
              attributeLabel={sizeLabel}
            />
          </div>
        </div>

        <div className="tko-publish__table-wrap tko-skc-img-wrap" data-field-error="mainImage">
          <table className="tko-publish__table tko-skc-img-table tko-product-img-table">
            <thead>
              <tr>
                <th className="tko-skc-img-table__color-head">{t('publishSpec.images')}</th>
                <th className="tko-product-img-table__main-head">
                  <span className="tko-th-with-help">
                    {t('publishSpec.mainImages')}{reqMark ? <> {reqMark}</> : null}
                    <FieldHelpMark text={specText('publishSpec.productImagesHintTt', tiktokProductImagesHint())} />
                  </span>
                </th>
                <th className="tko-product-img-table__detail-head">{t('publishSpec.otherDetails')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="tko-skc-img-table__color">
                  <strong>{t('publishSpec.images')}</strong>
                </td>
                <td className="tko-product-img-table__main">
                  <PublishImageUploadZone
                    compact
                    labelInside
                    label={t('publishSpec.mainStar')}
                    imageType={1}
                    value={mainImage ? [mainImage] : []}
                    shopId={shopId}
                    skipTiktokConvert={skipTiktokConvert}
                    enforceTiktokRules
                    onChange={(img) => onMainImageChange?.(img || null)}
                  />
                </td>
                <td className="tko-product-img-table__detail">
                  <div className="tko-skc-img-table__details">
                    <PublishImageUploadZone
                      compact
                      labelInside
                      label={t('publishSpec.detail')}
                      imageType={2}
                      multiple
                      maxCount={MAX_DETAIL_IMAGES}
                      uploadSlotFirst
                      prependNew
                      reorderable
                      value={Array.isArray(detailImages) ? detailImages : []}
                      shopId={shopId}
                      skipTiktokConvert={skipTiktokConvert}
                      enforceTiktokRules
                      onChange={(imgs) => onDetailImagesChange?.(
                        Array.isArray(imgs) ? imgs.slice(0, MAX_DETAIL_IMAGES) : [],
                      )}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {(typeof onMaterialImagesChange === 'function' && !hideMaterialImage)
        || typeof onVideosChange === 'function' ? (
        <div className="tko-media-stack" data-field-error={hideMaterialImage ? 'tiktokVideos' : 'materialImgUrl'}>
          {typeof onMaterialImagesChange === 'function' && !hideMaterialImage ? (
            <div className={`tko-field tko-media-stack__item${materialImgError ? ' tko-field--invalid' : ''}`}>
              <span className="tko-field__label tko-field__label-with-help">
                {t('publishSpec.material')}{reqMark ? <> {reqMark}</> : null}
                <FieldHelpMark text={specText('publishSpec.materialHintTt', tiktokMaterialHint())} />
              </span>
              <PublishImageUploadZone
                compact
                labelInside
                label={t('publishSpec.material')}
                imageType={1}
                imageRule="material"
                multiple={false}
                maxCount={1}
                enforceTiktokRules
                skipTiktokConvert={skipTiktokConvert}
                value={materialImage ? [materialImage] : []}
                shopId={shopId}
                onChange={onMaterialImagesChange}
              />
              {materialImgError ? (
                <p className="tko-wizard__inline-error" role="alert">{materialImgError}</p>
              ) : null}
            </div>
          ) : null}

          {typeof onVideosChange === 'function' ? (
            <div className={`tko-field tko-media-stack__item${videoError ? ' tko-field--invalid' : ''}`} data-field-error="tiktokVideos">
              <span className="tko-field__label tko-field__label-with-help">
                {t('publishSpec.mainVideo')}
                <FieldHelpMark text={specText('publishSpec.mainVideoHintTt', TIKTOK_MAIN_VIDEO_HINT)} />
              </span>
              <div className="tko-video-upload tko-video-upload--compact">
                {(Array.isArray(tiktokVideos) ? tiktokVideos : []).slice(0, 1).map((v) => {
                  const playSrc = String(v.videoUrl || v.ipeUrl || '').trim();
                  const posterSrc = String(v.coverUrl || '').trim();
                  return (
                    <div key={v.vid || playSrc || v.ipeUrl} className="tko-video-upload__thumb">
                      {posterSrc ? (
                        <img src={posterSrc} alt="" />
                      ) : playSrc ? (
                        <video src={playSrc} muted preload="metadata" playsInline />
                      ) : (
                        <span className="tko-video-upload__placeholder">{specText('publishSpec.video', fixedUi("视频"))}</span>
                      )}
                      <button
                        type="button"
                        className="tko-img-upload__remove"
                        aria-label={specText('publishSpec.removeVideo', '移除视频')}
                        disabled={videoBusy}
                        onClick={() => onVideosChange([])}
                      >
                        ×
                      </button>
                      {playSrc ? (
                        <button
                          type="button"
                          className="tko-img-upload__preview"
                          onClick={() => setVideoPreviewTarget({
                            name: t('publishSpec.mainVideo'),
                            videoUrl: playSrc,
                            outputVideoUrl: playSrc,
                            coverUrl: posterSrc,
                            posterUrl: posterSrc,
                            outputVideoPosterUrl: posterSrc,
                            previewUrl: posterSrc,
                            width: v.width,
                            height: v.height,
                          })}
                        >
                          {specText('publishSpec.preview', '预览')}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
                {!(tiktokVideos || []).length ? (
                  <button
                    ref={videoUploadAnchorRef}
                    type="button"
                    className={`tko-video-upload__drop${videoBusy ? ' tko-img-upload__drop--busy' : ''}`}
                    disabled={videoBusy || !shopId}
                    onClick={() => setVideoMenuOpen(true)}
                  >
                    {videoBusy ? (
                      <span className="tko-img-upload__busy" aria-live="polite">
                        <span className="tko-img-upload__spinner" aria-hidden />
                        <span className="tko-img-upload__busy-text">{specText('publishSpec.uploading', fixedUi("上传中"))}</span>
                      </span>
                    ) : (
                      <>
                        <span className="tko-img-upload__slot-label">{t('publishSpec.mainVideo')}</span>
                        <span className="tko-img-upload__slot-action">{specText('publishSpec.clickUpload', fixedUi("点击上传"))}</span>
                      </>
                    )}
                  </button>
                ) : null}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept={TIKTOK_PUBLISH_VIDEO_ACCEPT}
                  hidden
                  disabled={videoBusy}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) await uploadVideoFile(file);
                  }}
                />
                <TiktokUploadSourceMenu
                  open={videoMenuOpen}
                  anchorRef={videoUploadAnchorRef}
                  onClose={() => setVideoMenuOpen(false)}
                  disabled={videoBusy}
                  localLabel={specText('publishSpec.localFile', '本地文件')}
                  imageSpaceLabel={specText('publishSpec.videoSpace', '视频空间')}
                  onPickLocal={() => videoInputRef.current?.click()}
                  onPickImageSpace={() => setVideoLibraryOpen(true)}
                />
                <LibraryModalContent
                  isOpen={videoLibraryOpen}
                  mediaKind="video"
                  onClose={() => {
                    setVideoLibraryOpen(false);
                    setVideoLibrarySelection([]);
                  }}
                  images={[]}
                  selection={videoLibrarySelection}
                  onToggle={(id) => {
                    setVideoLibrarySelection((prev) => (
                      prev.includes(id) ? [] : [id]
                    ));
                  }}
                  maxSelection={1}
                  onConfirm={handleVideoLibraryConfirm}
                  onImagesAppended={rememberImageSpaceRows}
                  uploadOriginSource="upload"
                  showToast={showToast}
                  deferUsageBump
                  selectAllLabel={specText('publishSpec.selectLoaded', '全选已加载')}
                  closeOnBackdropClick={false}
                />
                <VideoSpacePreviewModal
                  open={Boolean(videoPreviewTarget)}
                  video={videoPreviewTarget}
                  navList={videoPreviewTarget ? [videoPreviewTarget] : []}
                  onClose={() => setVideoPreviewTarget(null)}
                />
              </div>
              {videoError ? (
                <p className="tko-wizard__inline-error" role="alert">{videoError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {afterMaterialContent}

      {/* —— 供应信息 —— */}
      {hasColors ? (
        <div className="tko-spec-block">
          <h4 className="tko-publish__subhead">{t('publishSpec.supply')}</h4>
          {isEdit ? (
            <p className="tko-wizard__inline-hint">
              {priceMode === 'sale'
                ? ''
                : priceMode === 'cost'
                  ? t('publishSpec.costHint')
                  : t('publishSpec.priceHint')}
              {priceLockedHint ? ` ${priceLockedHint}` : ''}
            </p>
          ) : null}

          <div className="tko-publish__table-wrap">
            <table className="tko-publish__table tko-supply-table">
              <thead>
                <tr>
                  <th className="tko-supply-table__color">{colorLabel}</th>
                  <th className="tko-supply-table__sku-img">
                    <span className="tko-th-with-help">
                      {t('publishSpec.variant')}
                      <FieldHelpMark text={specText('publishSpec.variantHintTt', tiktokVariantImageHint())} />
                    </span>
                  </th>
                  <th className="tko-supply-table__size">{sizeLabel}</th>
                  {priceEditable ? (
                    <BatchHeader onBatch={() => setBatchField('price')}>
                      {priceHeadLabel}
                    </BatchHeader>
                  ) : (
                    <th>{priceHeadLabel}</th>
                  )}
                  <BatchHeader onBatch={() => setBatchField('suggestedPrice')}>
                    {t('publishSpec.msrp')}
                  </BatchHeader>
                  <BatchHeader onBatch={() => setBatchField('stock')}>{t('productTable.stock')}{reqMark ? <> {reqMark}</> : null}</BatchHeader>
                  <BatchHeader onBatch={() => setBatchField('supplierSku')}>{t('publishSpec.merchantSku')}</BatchHeader>
                  <BatchHeader onBatch={() => setBatchField('identifierCode')}>{t('publishSpec.barcode')}</BatchHeader>
                </tr>
              </thead>
              <tbody>
                {coloredSkcs.flatMap((skc) => {
                  const rows = skc.skuRows?.length ? skc.skuRows : [{ id: `${skc.id}-empty` }];
                  // 变体图优先 skc.skuImage；旧草稿曾写在色块 mainImage
                  const variantImg = skc.skuImage || skc.mainImage || null;
                  return rows.map((sku, rowIndex) => {
                    const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                      ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                      : (
                        attributeValueDisplayLabel(
                          sizeOptions.find((v) => String(v.id) === String(sku.sizeValueId))
                          || sku.sizeLabel,
                        ) || '—'
                      );
                    return (
                      <tr key={sku.id || `${skc.id}-${rowIndex}`}>
                        {rowIndex === 0 ? (
                          <td rowSpan={rows.length} className="tko-supply-table__color">
                            <strong>{skc.colorLabel || '—'}</strong>
                            {skc.skcCode ? (
                              <div className="tko-skc-img-table__skc-id">{skc.skcCode}</div>
                            ) : null}
                          </td>
                        ) : null}
                        {rowIndex === 0 ? (
                          <td rowSpan={rows.length} className="tko-supply-table__sku-img">
                            <PublishImageUploadZone
                              compact
                              labelInside
                              label={t('publishSpec.variant')}
                              imageType={1}
                              imageRule="material"
                              useCase={TIKTOK_ATTRIBUTE_IMAGE_USE_CASE}
                              value={variantImg ? [variantImg] : []}
                              shopId={shopId}
                              skipTiktokConvert={skipTiktokConvert}
                              enforceTiktokRules
                              onChange={(img) => onUpdateSkc(skc.id, {
                                skuImage: img || null,
                                // 旧草稿兼容：色块级 mainImage 不再承担商品主图
                                colorBlockImage: null,
                                squareImage: null,
                              })}
                              hint=""
                            />
                          </td>
                        ) : null}
                        <td className="tko-supply-table__size">{sizeName}</td>
                        <td>
                          <div className="tko-price-cell">
                            <input
                              className="tko-input"
                              value={sku.price ?? ''}
                              disabled={!priceEditable}
                              title={!priceEditable ? (priceLockedHint || t('publishSpec.priceLockedTitle')) : undefined}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, { price: e.target.value })}
                              placeholder="0.00"
                            />
                            {currency ? <span className="tko-price-cell__unit">{currency}</span> : null}
                          </div>
                        </td>
                        <td>
                          <div className="tko-price-cell">
                            <input
                              className="tko-input"
                              value={sku.suggestedPrice ?? ''}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, {
                                suggestedPrice: e.target.value,
                                suggestedPriceCurrencyType: currency || sku.suggestedPriceCurrencyType || 'USD',
                              })}
                              placeholder={specText('publishPkg.optionalPh', '选填')}
                              inputMode="decimal"
                            />
                            <span className="tko-price-cell__unit">{currency || 'USD'}</span>
                          </div>
                        </td>
                        <td>
                          <input
                            className="tko-input"
                            value={sku.stock ?? ''}
                            onChange={(e) => onUpdateSku(skc.id, sku.id, { stock: e.target.value })}
                            placeholder={t('publishSpec.fill')}
                          />
                        </td>
                        <td>
                          <input
                            className="tko-input"
                            value={sku.supplierSku ?? ''}
                            maxLength={TIKTOK_SELLER_SKU_MAX_LEN}
                            onChange={(e) => onUpdateSku(skc.id, sku.id, {
                              supplierSku: String(e.target.value || '').replace(/\s/g, ''),
                            })}
                            placeholder={specText('publishSpec.sellerSkuPh', `seller_sku（≤${TIKTOK_SELLER_SKU_MAX_LEN}，无空格）`, { max: TIKTOK_SELLER_SKU_MAX_LEN })}
                            title={specText('publishSpec.sellerSkuTitle', 'Create Product seller_sku：1–50 字符，不能含空格')}
                          />
                        </td>
                        <td>
                          <div className="tko-barcode-cell">
                            <select
                              className="tko-input tko-barcode-cell__type"
                              value={sku.identifierType || 'GTIN'}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, { identifierType: e.target.value })}
                            >
                              {TIKTOK_IDENTIFIER_CODE_TYPES.map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                            <input
                              className="tko-input"
                              value={sku.identifierCode ?? ''}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, { identifierCode: e.target.value })}
                              placeholder={specText('publishSpec.barcodeExample', '选填，如 1234567890123')}
                              inputMode="text"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>

          {batchField === 'supplierSku' ? (
            <SupplierCodeBatchModal
              title={specText('publishSpec.batchMerchantSku', fixedUi("批量填写商家 SKU"))}
              colors={supplierSkuColorOptions}
              defaultSpu={defaultSupplierSkuSpu}
              skcList={skcList}
              onClose={() => setBatchField(null)}
              onConfirm={applySupplierCodeBatch}
            />
          ) : batchField === 'identifierCode' ? (
            <BarcodeBatchModal
              sizes={barcodeSizeOptions}
              defaultType={
                coloredSkcs.find((skc) => (skc.skuRows || []).some((r) => r.identifierType))
                  ?.skuRows?.find((r) => r.identifierType)?.identifierType
                || 'GTIN'
              }
              onClose={() => setBatchField(null)}
              onConfirm={applyBarcodeBatch}
            />
          ) : batchField ? (
            <BatchFillModal
              field={batchField}
              currency={currency}
              titleOverride={batchField === 'price' ? priceBatchTitle : undefined}
              onClose={() => setBatchField(null)}
              onConfirm={applyBatch}
            />
          ) : null}
        </div>
      ) : null}

      <PrintInfringementResultModal
        open={printRiskModalOpen}
        riskLabel={printRiskMeta.riskLabel}
        assessedAt={printRiskMeta.meta.assessedAt}
        body={printRiskMeta.meta.riskAdvice}
        onClose={() => setPrintRiskModalOpen(false)}
      />
    </div>
  );
}
